import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, BookOpen, Users, TrendingUp, Clock, Edit2, Eye, Trash2, Undo2, Send, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNodes, useSubmitForReview, useApproveContent, useRejectContent } from '@/hooks/useNodes';
import { useDeleteNode } from '@/hooks/useNodeMutations';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { useAuth } from '@/hooks/useAuth';
import NodeCard from '@/components/content/NodeCard';
import NodeEditor from '@/components/content/NodeEditor';
import { toast } from 'sonner';
import type { Tables } from '@/integrations/supabase/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

type Node = Tables<'nodes'>;

const EditorialManagement: React.FC = () => {
  const { user } = useAuth();
  const { canCreateContent, canEditContent } = useRoleAccess();
  const { data: allNodes } = useNodes();
  const [showEditor, setShowEditor] = useState(false);
  const [editingNode, setEditingNode] = useState<Node | undefined>();
  
  // Mutations
  const deleteNodeMutation = useDeleteNode();
  const submitForReviewMutation = useSubmitForReview();
  const approveContentMutation = useApproveContent();
  const rejectContentMutation = useRejectContent();

  // Redirect non-editors to the contributor dashboard
  if (!canEditContent) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Restricted</h1>
          <p className="text-black mb-4">
            Editorial management requires editor access or higher.
          </p>
          <Button asChild>
            <Link to="/content">Go to My Content</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Compute stats from allNodes
  const myNodes = allNodes?.filter(node => node.author_id === user?.id) || [];
  const draftNodes = allNodes?.filter(node => node.status === 'draft') || [];
  const reviewNodes = allNodes?.filter(node => node.status === 'in_review') || [];
  const publishedNodes = allNodes?.filter(node => node.status === 'published') || [];

  const stats = [
    {
      title: 'Total Content',
      value: allNodes?.length || 0,
      icon: BookOpen,
      color: 'text-black',
    },
    {
      title: 'Published',
      value: publishedNodes?.length || 0,
      icon: TrendingUp,
      color: 'text-green-600',
    },
    {
      title: 'In Review',
      value: reviewNodes?.length || 0,
      icon: Clock,
      color: 'text-black',
    },
    {
      title: 'My Content',
      value: myNodes?.length || 0,
      icon: Users,
      color: 'text-black',
    },
  ];

  const handleCreateNew = () => {
    setEditingNode(undefined);
    setShowEditor(true);
  };

  const handleEditNode = (node: Node) => {
    setEditingNode(node);
    setShowEditor(true);
  };

  const handleSaveComplete = (savedNode?: Node) => {
    setShowEditor(false);
    setEditingNode(undefined);
    if (savedNode) {
      toast.success(editingNode ? 'Content updated successfully' : 'Content created successfully');
    }
  };

  const handleCancel = () => {
    setShowEditor(false);
    setEditingNode(undefined);
  };

  const handleDeleteContent = async (node: Node) => {
    try {
      await deleteNodeMutation.mutateAsync(node.id);
      toast.success('Content deleted successfully');
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Failed to delete content');
    }
  };

  const handleReturnToContributor = async (node: Node) => {
    try {
      await rejectContentMutation.mutateAsync({
        nodeId: node.id,
        feedback: 'Returned to contributor for revision'
      });
      toast.success('Content returned to contributor');
    } catch (error) {
      console.error('Error returning content:', error);
      toast.error('Failed to return content to contributor');
    }
  };

  const handleApproveContent = async (node: Node) => {
    try {
      await approveContentMutation.mutateAsync(node.id);
      toast.success('Content approved and published');
    } catch (error) {
      console.error('Error approving content:', error);
      toast.error('Failed to approve content');
    }
  };

  const renderContentGrid = (nodes: Node[], emptyMessage: string, showActions = true) => {
    if (!nodes || nodes.length === 0) {
      return (
        <div className="text-center py-12">
          <BookOpen className="h-16 w-16 mx-auto mb-4 text-black/50" />
          <p className="text-black mb-4">{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {nodes.map((node) => (
          <div key={node.id} className="relative group">
            <NodeCard node={node} />
            
            {/* Status Badge */}
            <div className="absolute top-2 right-2 flex gap-1 opacity-100">
              <Badge variant={
                node.status === 'published' ? 'default' :
                node.status === 'in_review' ? 'secondary' :
                node.status === 'archived' ? 'destructive' :
                'outline'
              }>
                {node.status}
              </Badge>
            </div>

            {/* Action Buttons */}
            {showActions && (
              <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* View Button */}
                <Button size="sm" variant="outline" asChild>
                  <Link to={`/content/view/${node.id}`}>
                    <Eye className="h-3 w-3" />
                  </Link>
                </Button>

                {/* Edit Button */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEditNode(node)}
                  title="Edit content"
                >
                  <Edit2 className="h-3 w-3" />
                </Button>

                {/* Review Actions for content in review */}
                {node.status === 'in_review' && (
                  <>
                    <Button
                      size="sm"
                      onClick={() => handleApproveContent(node)}
                      disabled={approveContentMutation.isPending}
                      title="Approve and publish"
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Send className="h-3 w-3" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReturnToContributor(node)}
                      disabled={rejectContentMutation.isPending}
                      title="Return to contributor"
                      className="border-gray-200 text-black hover:bg-white"
                    >
                      <Undo2 className="h-3 w-3" />
                    </Button>
                  </>
                )}

                {/* Delete Button - only for drafts and own content */}
                {(node.status === 'draft' || node.author_id === user?.id) && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        title="Delete content"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Content</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{node.title}"? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteContent(node)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (showEditor) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={handleCancel} className="mb-4">
            ← Back to Editorial Dashboard
          </Button>
          <h1 className="text-3xl font-bold">
            {editingNode ? 'Edit Content' : 'Create New Content'}
          </h1>
          <p className="text-black mt-1">
            Editorial content creation and management
          </p>
        </div>
        <NodeEditor
          node={editingNode}
          onSave={handleSaveComplete}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold">Editorial Dashboard</h1>
          <p className="text-black mt-1">
            Review, approve, and manage content across the platform
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={handleCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            Create Content
          </Button>
          <Button variant="outline" asChild>
            <Link to="/content">
              <Edit2 className="h-4 w-4 mr-2" />
              My Content
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-black">
                    {stat.title}
                  </p>
                  <p className="text-2xl font-bold">{stat.value}</p>
                </div>
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Content Tabs */}
      <Tabs defaultValue="review" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="review">
            Review Queue ({reviewNodes.length})
          </TabsTrigger>
          <TabsTrigger value="all">
            All Content ({allNodes?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="published">
            Published ({publishedNodes.length})
          </TabsTrigger>
          <TabsTrigger value="drafts">
            Drafts ({draftNodes.length})
          </TabsTrigger>
          <TabsTrigger value="my-content">
            My Content ({myNodes.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="review" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Content Awaiting Review</h3>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-black" />
              <span className="text-sm text-black">
                Review and approve or return to contributors
              </span>
            </div>
          </div>
          {renderContentGrid(reviewNodes, "No content awaiting review")}
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">All Content</h3>
            <span className="text-sm text-black">
              Complete platform content overview
            </span>
          </div>
          {renderContentGrid(allNodes || [], "No content available")}
        </TabsContent>

        <TabsContent value="published" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Published Content</h3>
            <span className="text-sm text-black">
              Live and accessible to users
            </span>
          </div>
          {renderContentGrid(publishedNodes, "No published content")}
        </TabsContent>

        <TabsContent value="drafts" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Draft Content</h3>
            <span className="text-sm text-black">
              Work in progress across the platform
            </span>
          </div>
          {renderContentGrid(draftNodes, "No draft content")}
        </TabsContent>

        <TabsContent value="my-content" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">My Content</h3>
            <span className="text-sm text-black">
              Content you've created
            </span>
          </div>
          {renderContentGrid(myNodes, "You haven't created any content yet")}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EditorialManagement;
